/**
 * Regel-/Registrierungs-Seite – /register  (NT-ALPHA-REWORK-01)
 *
 * Ersetzt das frühere Bewerbungsformular. Self-Service: E-Mail + Benutzername + Passwort
 * plus fünf Pflicht-Bestätigungen. Kein Bewerbungs-Charakter, kein NDA.
 * Nach dem Absenden: "Bestätige deine E-Mail"-Seite (kein Login möglich bis verifiziert).
 *
 * Client-seitige Passwort-Stärken-Anzeige ist nur UX – serverseitige Validierung ist maßgeblich.
 */

import { navigate } from '../router.js';
import { gameState } from '../state/gameState.js';
import { registerUser, resendVerification } from '../services/auth.js';
import { ApiError } from '../services/api.js';

const DISCORD_INVITE_URL = 'https://discord.gg/Gz74HUTqdW';

// Muss mit TOKEN_EXPIRY_HOURS in auth.service.ts übereinstimmen (kein Shared-Import
// für reine Anzeige-Werte — etabliertes Repo-Muster, NT-REG-FUNNEL-01).
const VERIFY_LINK_VALID_HOURS = 24;

// Passwort-Kriterien entsprechen der server-seitigen Validierung in auth.schema.ts
const CRITERIA: readonly { readonly label: string; readonly test: (pw: string) => boolean }[] = [
  { label: 'Mindestens 12 Zeichen', test: (pw) => pw.length >= 12 },
  { label: 'Großbuchstabe (A–Z)', test: (pw) => /[A-Z]/.test(pw) },
  { label: 'Zahl (0–9)', test: (pw) => /[0-9]/.test(pw) },
  { label: 'Sonderzeichen (!@#...)', test: (pw) => /[^A-Za-z0-9]/.test(pw) },
];

// Die fünf Pflicht-Checkbox-IDs (Button bleibt gesperrt bis alle gesetzt sind)
const CONFIRM_IDS = ['reg-rules', 'reg-privacy', 'reg-content', 'reg-reset', 'reg-age'] as const;

/** E-Mail für die Anzeige maskieren (z.B. "spieler@beispiel.de" → "sp****@beispiel.de"). */
function maskEmail(email: string): string {
  const at = email.indexOf('@');
  if (at <= 0) return '***';
  const local = email.slice(0, at);
  const domain = email.slice(at);
  const visible = local.slice(0, Math.min(2, local.length));
  return `${visible}****${domain}`;
}

export function renderRegister(): void {
  // Falls bereits eingeloggt: Warteliste-Spieler zur Warteliste, sonst zum Dashboard
  if (gameState.isAuthenticated()) {
    navigate(gameState.isWaitlisted() ? '/warteliste' : '/dashboard');
    return;
  }

  const app = document.getElementById('app');
  if (!app) return;

  app.innerHTML = `
    <main class="auth-page">
      <div class="auth-card">
        <p class="auth-brand">&#9654; NIGHTTRADE // CLOSED ALPHA</p>
        <h1 class="auth-title">Konto erstellen</h1>
        <p class="auth-subtitle">Plätze begrenzt — Registrierung direkt möglich</p>

        <ul class="rules-info-list">
          <li>Zugang während der Closed Alpha kann jederzeit eingeschränkt werden.</li>
          <li>Alpha-Fortschritt wird zum Release vollständig zurückgesetzt.</li>
          <li>Screenshots, Videos und Streams nur mit Freigabe. Partner-Streamer ausgenommen.</li>
        </ul>

        <a
          href="${DISCORD_INVITE_URL}"
          target="_blank"
          rel="noopener noreferrer"
          class="rules-discord-link"
        >→ Discord beitreten (optional, Community &amp; Support)</a>

        <form id="reg-form" novalidate>
          <div class="form-group">
            <label for="reg-email">E-Mail-Adresse</label>
            <input
              type="email"
              id="reg-email"
              name="email"
              autocomplete="email"
              required
              placeholder="spieler@beispiel.de"
            />
          </div>

          <div class="form-group">
            <label for="reg-username">Benutzername</label>
            <input
              type="text"
              id="reg-username"
              name="username"
              autocomplete="username"
              required
              placeholder="3–20 Zeichen"
              minlength="3"
              maxlength="20"
            />
            <p class="form-help">3–20 Zeichen, Buchstaben, Zahlen, Bindestrich</p>
          </div>

          <div class="form-group">
            <label for="reg-password">Passwort</label>
            <div class="pw-input-wrap">
              <input
                type="password"
                id="reg-password"
                name="password"
                autocomplete="new-password"
                required
              />
              <button type="button" class="pw-toggle-btn" id="reg-pw-toggle" aria-label="Passwort anzeigen">ANZEIGEN</button>
            </div>
            <div class="pw-strength" id="pw-strength" aria-live="polite">
              <div class="pw-bars">
                <div class="pw-bar" id="pw-bar-0"></div>
                <div class="pw-bar" id="pw-bar-1"></div>
                <div class="pw-bar" id="pw-bar-2"></div>
                <div class="pw-bar" id="pw-bar-3"></div>
              </div>
              <ul class="pw-criteria" aria-label="Passwort-Anforderungen">
                <li class="pw-criterion" id="pw-criterion-0">Mindestens 12 Zeichen</li>
                <li class="pw-criterion" id="pw-criterion-1">Großbuchstabe (A–Z)</li>
                <li class="pw-criterion" id="pw-criterion-2">Zahl (0–9)</li>
                <li class="pw-criterion" id="pw-criterion-3">Sonderzeichen (!@#...)</li>
              </ul>
            </div>
          </div>

          <div class="form-group">
            <label for="reg-password-confirm">Passwort bestätigen</label>
            <div class="pw-input-wrap">
              <input
                type="password"
                id="reg-password-confirm"
                name="passwordConfirm"
                autocomplete="new-password"
                required
              />
              <button type="button" class="pw-toggle-btn" id="reg-pw-confirm-toggle" aria-label="Passwort-Bestätigung anzeigen">ANZEIGEN</button>
            </div>
          </div>

          <fieldset class="rules-checkboxes">
            <legend>Pflichtbestätigungen</legend>

            <label class="checkbox-label">
              <input type="checkbox" id="reg-rules" name="rulesAccepted" required />
              <span>Ich habe die <a href="/agb#paragraph-10">Alpha-Regeln (§ 10 AGB)</a> gelesen und akzeptiere sie.</span>
            </label>

            <label class="checkbox-label">
              <input type="checkbox" id="reg-privacy" name="privacyAccepted" required />
              <span>Ich stimme der <a href="/datenschutz">Datenschutzerklärung</a> zu.</span>
            </label>

            <label class="checkbox-label">
              <input type="checkbox" id="reg-content" name="contentRestrictionAccepted" required />
              <span>Ich verstehe, dass Streams/Videos nur mit ausdrücklicher Freigabe erlaubt sind
                (Partner-Streamer ausgenommen).</span>
            </label>

            <label class="checkbox-label">
              <input type="checkbox" id="reg-reset" name="resetNoticeAccepted" required />
              <span>Ich bestätige, dass mein Alpha-Fortschritt zum Release vollständig zurückgesetzt wird.</span>
            </label>

            <label class="checkbox-label">
              <input type="checkbox" id="reg-age" name="ageConfirmed" required />
              <span>Ich bin mindestens 18 Jahre alt.</span>
            </label>
          </fieldset>

          <p class="waitlist-note">
            Alpha-Plätze sind begrenzt. Bleibt dein Konto 30 Tage ohne Spielaktivität (ein
            Login allein reicht nicht), kann dein Platz neu vergeben werden. Der Spielstand
            bleibt erhalten.
          </p>

          <div
            id="reg-error"
            class="form-error"
            role="alert"
            aria-live="polite"
            hidden
          ></div>

          <button type="submit" id="reg-submit" class="primary btn-full" disabled>
            Konto erstellen
          </button>
        </form>

        <nav class="auth-links">
          <a href="/login">Bereits registriert? &#8594; Anmelden</a>
        </nav>
      </div>
    </main>
  `;

  // DOM-Referenzen holen
  const form = document.getElementById('reg-form') as HTMLFormElement | null;
  const emailInput = document.getElementById('reg-email') as HTMLInputElement | null;
  const usernameInput = document.getElementById('reg-username') as HTMLInputElement | null;
  const passwordInput = document.getElementById('reg-password') as HTMLInputElement | null;
  const passwordConfirmInput = document.getElementById('reg-password-confirm') as HTMLInputElement | null;
  const submitBtn = document.getElementById('reg-submit') as HTMLButtonElement | null;
  const errorDiv = document.getElementById('reg-error') as HTMLDivElement | null;

  const confirmBoxes = CONFIRM_IDS.map((id) => document.getElementById(id) as HTMLInputElement | null);

  if (
    !form || !emailInput || !usernameInput || !passwordInput ||
    !passwordConfirmInput || !submitBtn || !errorDiv ||
    confirmBoxes.some((c) => c === null)
  ) return;

  // Passwort-Toggles
  const pwToggle = document.getElementById('reg-pw-toggle') as HTMLButtonElement | null;
  const pwConfirmToggle = document.getElementById('reg-pw-confirm-toggle') as HTMLButtonElement | null;
  pwToggle?.addEventListener('click', (): void => {
    const isHidden = passwordInput.type === 'password';
    passwordInput.type = isHidden ? 'text' : 'password';
    pwToggle.textContent = isHidden ? 'VERBERGEN' : 'ANZEIGEN';
  });
  pwConfirmToggle?.addEventListener('click', (): void => {
    const isHidden = passwordConfirmInput.type === 'password';
    passwordConfirmInput.type = isHidden ? 'text' : 'password';
    pwConfirmToggle.textContent = isHidden ? 'VERBERGEN' : 'ANZEIGEN';
  });

  // Strength indicator DOM-Referenzen (per Kriterium-Index)
  const pwBars: (HTMLElement | null)[] = CRITERIA.map((_, i) =>
    document.getElementById(`pw-bar-${String(i)}`),
  );
  const pwCriteriaItems: (HTMLElement | null)[] = CRITERIA.map((_, i) =>
    document.getElementById(`pw-criterion-${String(i)}`),
  );

  function updatePasswordStrength(password: string): void {
    const metCount = CRITERIA.filter((c) => c.test(password)).length;
    pwBars.forEach((bar, i) => {
      bar?.classList.toggle('pw-bar--met', i < metCount);
    });
    CRITERIA.forEach((criterion, i) => {
      pwCriteriaItems[i]?.classList.toggle('pw-criterion--met', criterion.test(password));
    });
  }

  // Button erst freigeben, wenn alle fünf Pflicht-Checkboxen gesetzt sind
  function updateSubmitEnabled(): void {
    if (!submitBtn) return;
    const allChecked = confirmBoxes.every((c) => c?.checked === true);
    submitBtn.disabled = !allChecked;
  }
  confirmBoxes.forEach((c) => c?.addEventListener('change', updateSubmitEnabled));

  function showError(message: string): void {
    if (!errorDiv) return;
    errorDiv.textContent = message;
    errorDiv.hidden = false;
  }

  function hideError(): void {
    if (!errorDiv) return;
    errorDiv.textContent = '';
    errorDiv.hidden = true;
  }

  function setLoading(loading: boolean): void {
    if (!submitBtn) return;
    submitBtn.disabled = loading;
    submitBtn.textContent = loading ? '■ WIRD ERSTELLT...' : 'Konto erstellen';
    submitBtn.setAttribute('aria-busy', String(loading));
  }

  /** "Bestätige deine E-Mail"-Seite (kein Login möglich bis verifiziert). */
  function showCheckEmailPage(email: string): void {
    if (!app) return;
    app.innerHTML = `
      <main class="auth-page">
        <div class="auth-card">
          <p class="auth-brand">&#9654; NIGHTTRADE // CLOSED ALPHA</p>
          <h1 class="auth-title">Fast geschafft</h1>
          <div class="form-success" role="status">
            Wir haben eine E-Mail an <strong>${maskEmail(email)}</strong> geschickt.
            Bitte klicke auf den Link in der E-Mail, um dein Konto zu aktivieren.
            Erst danach kannst du dich anmelden.
          </div>

          <p class="form-help">
            Keine Mail im Posteingang? Bitte auch im <strong>Spam-Ordner</strong> nachsehen.
            Der Link ist ${String(VERIFY_LINK_VALID_HOURS)} Stunden gültig.
          </p>

          <p class="form-help" id="reg-resend-status" aria-live="polite"></p>

          <button type="button" id="reg-resend-btn" class="primary btn-full">
            Keine E-Mail erhalten? Erneut senden
          </button>

          <nav class="auth-links">
            <a href="/login">&#8594; Zum Login</a>
          </nav>
        </div>
      </main>
    `;

    const resendBtnEl = document.getElementById('reg-resend-btn') as HTMLButtonElement | null;
    const resendStatus = document.getElementById('reg-resend-status') as HTMLParagraphElement | null;
    if (!resendBtnEl) return;
    // Non-null-Const für den Gebrauch in verschachtelten Closures (Narrowing propagiert dort nicht)
    const btn = resendBtnEl;

    // 2-Minuten-Cooldown mit Countdown (Server erzwingt zusätzlich ein eigenes Limit)
    let cooldownTimer: ReturnType<typeof setInterval> | null = null;
    function startCooldown(): void {
      let remaining = 120;
      btn.disabled = true;
      const tick = (): void => {
        if (remaining <= 0) {
          if (cooldownTimer) clearInterval(cooldownTimer);
          btn.disabled = false;
          btn.textContent = 'Keine E-Mail erhalten? Erneut senden';
          return;
        }
        btn.textContent = `Erneut senden möglich in ${String(remaining)}s`;
        remaining -= 1;
      };
      tick();
      cooldownTimer = setInterval(tick, 1000);
    }

    btn.addEventListener('click', (): void => {
      if (resendStatus) resendStatus.textContent = '';
      startCooldown();
      void (async (): Promise<void> => {
        try {
          await resendVerification(email);
          if (resendStatus) {
            resendStatus.textContent = 'Falls noch eine Bestätigung aussteht, wurde der Link erneut gesendet.';
          }
        } catch (err: unknown) {
          if (resendStatus) {
            resendStatus.textContent =
              err instanceof ApiError && err.status === 429
                ? 'Zu viele Versuche. Bitte kurz warten.'
                : 'Erneut senden fehlgeschlagen. Bitte später versuchen.';
          }
        }
      })();
    });
  }

  passwordInput.addEventListener('input', (): void => {
    updatePasswordStrength(passwordInput.value);
  });

  form.addEventListener('submit', (event: Event): void => {
    event.preventDefault();
    hideError();

    const email = emailInput.value.trim();
    const username = usernameInput.value.trim();
    const password = passwordInput.value;
    const passwordConfirm = passwordConfirmInput.value;

    // Client-seitige Vorvalidierung (Serverseitige Validierung ist maßgeblich)
    if (!email) {
      showError('Bitte E-Mail-Adresse eingeben.');
      emailInput.focus();
      return;
    }

    if (!username) {
      showError('Bitte Benutzernamen eingeben.');
      usernameInput.focus();
      return;
    }

    if (!password) {
      showError('Bitte Passwort eingeben.');
      passwordInput.focus();
      return;
    }

    if (password !== passwordConfirm) {
      showError('Die Passwörter stimmen nicht überein.');
      passwordConfirmInput.focus();
      return;
    }

    if (!confirmBoxes.every((c) => c?.checked === true)) {
      showError('Bitte alle Pflichtbestätigungen setzen.');
      return;
    }

    setLoading(true);

    void (async (): Promise<void> => {
      try {
        await registerUser(email, username, password, {
          rulesAccepted: true,
          privacyAccepted: true,
          contentRestrictionAccepted: true,
          resetNoticeAccepted: true,
          ageConfirmed: true,
        });
        showCheckEmailPage(email);
      } catch (err: unknown) {
        setLoading(false);
        updateSubmitEnabled();
        if (err instanceof ApiError) {
          if (err.status === 429) {
            showError('Zu viele Registrierungsversuche. Bitte später erneut versuchen.');
          } else if (err.code === 'EMAIL_ALREADY_REGISTERED' || err.code === 'REGISTRATION_FAILED') {
            // Timing-safe: gleiche Erfolgsmeldung (verhindert User-Enumeration)
            showCheckEmailPage(email);
          } else if (err.code === 'USERNAME_TAKEN') {
            showError('Dieser Benutzername ist bereits vergeben.');
            usernameInput.focus();
          } else if (err.code === 'VALIDATION_ERROR') {
            // Server-seitige Validierungsfehler: erstes fehlerhaftes Feld anzeigen und fokussieren
            const rawIssues = err.details.issues;
            if (Array.isArray(rawIssues) && rawIssues.length > 0) {
              const first = rawIssues[0] as Record<string, unknown>;
              const field = typeof first.field === 'string' ? first.field : '';
              const msg = typeof first.message === 'string' ? first.message : err.message;
              showError(msg);
              if (field === 'email') emailInput.focus();
              else if (field === 'username') usernameInput.focus();
              else if (field === 'password') passwordInput.focus();
            } else {
              showError(err.message);
            }
          } else {
            showError('Registrierung fehlgeschlagen. Bitte versuche es erneut.');
          }
        } else {
          showError('Verbindungsfehler. Bitte versuche es erneut.');
        }
      }
    })();
  });
}
